/**
 * 차트 데이터 관련 Mixin
 * 차트 데이터 설정 및 관리
 */
import backEndApi from "@src/api/backEndApi";
import trendFunction from "../TrendFunction.js";

export default {
    methods: {
        setChartOrder() {
            // 트랜드 2형일때만 차트 순서 조정후 api call을 통해 저장하기위함
            this.onChangeOrderTrendSecond();
        },
        setBaseChartData(data) {
            if (!data || !this.sortedPropsArr) {
                this.baseChartData = data;
                return;
            }

            const propIndex = Object.fromEntries(this.sortedPropsArr.map((key, idx) => [key, idx]));

            const sorted = [...data].sort((a, b) => {
                const idxA = propIndex[a.key] ?? Infinity;
                const idxB = propIndex[b.key] ?? Infinity;
                return idxA - idxB;
            });

            this.baseChartData = sorted;
        },
        setChartDataView(data) {
            if (this.currentNode.nodeType == "System") return;
            this.chartView = [];
            let propCodeSet = new Set();
            let filterIoProp = [];
            let filterCalcProp = [];

            if (this.currentNode.nodeType == "Group") {
                if (data[0]?.ioProps) {
                    filterIoProp = data[0].ioProps.filter((col) =>
                        this.equipTypeDetail.ioProps.some((type) => type.propCode == col.propCode)
                    );
                }
                if (data[0]?.calcProps) {
                    filterCalcProp = data[0].calcProps.filter((col) =>
                        this.equipTypeDetail.calcProps.some((type) => type.propCode == col.propCode)
                    );
                }

                filterIoProp && filterIoProp.forEach((ioProp) => propCodeSet.add(ioProp.propCode));
                filterCalcProp && filterCalcProp.forEach((calcProp) => propCodeSet.add(calcProp.propCode));
            } else {
                if (data.ioProps)
                    filterIoProp = data.ioProps.filter((col) =>
                        this.equipTypeDetail.ioProps.some((type) => type.propCode == col.propCode)
                    );
                if (data.calcProps)
                    filterCalcProp = data.calcProps.filter((col) =>
                        this.equipTypeDetail.calcProps.some((type) => type.propCode == col.propCode)
                    );

                filterIoProp.forEach((ioProp) => propCodeSet.add(ioProp.propCode));
                filterCalcProp.forEach((calcProp) => propCodeSet.add(calcProp.propCode));
            }
            this.chartView = Array.from(propCodeSet).map((code) => ({
                value: code,
                checked: true,
            }));
        },
        async setRegressionData(rltdIndex) {
            if (!rltdIndex) return;

            if (!this.isEmpty(this.rltdTrendData.results) && !this.isEmpty(this.sumNodeTrend.results)) {
                // regDt 제거
                this.rltdTrendData.results.forEach((rltd) => {
                    delete rltd.regDt;
                });

                const xRaw = this.rltdTrendData.results.map((item) => item[rltdIndex] || 0);
                const yRaw = this.sumNodeTrend.results
                    .map((item) => {
                        const idx = this.sumNodeTrend.headers.sysNodeIdx;
                        return item[idx];
                    })
                    .filter((i) => i !== null);

                const minLength = Math.min(xRaw.length, yRaw.length);
                const xVar = xRaw.slice(0, minLength);
                const yVar = yRaw.slice(0, minLength);

                if (minLength > 0) {
                    try {
                        this.regressionResult = Math.LinearRegression.runAnalysis2([xVar], yVar);
                        this.regressionResult.resultMsg = Math.LinearRegression.evalResult(this.regressionResult);
                        this.predictData = Math.LinearRegression.predict2([xVar], this.regressionResult);
                    } catch (err) {
                        console.error("회귀 분석 오류:", err);
                        this.regressionResult = null;
                        this.predictData = [];
                    }
                } else {
                    console.warn("회귀 분석을 위한 유효한 데이터가 없습니다.");
                    this.regressionResult = null;
                    this.predictData = [];
                }
            } else {
                this.regressionResult = null;
                this.predictData = [];
            }
        },
        async getSumNodeTrendAll(node, date) {
            let statusParams = {
                sysNodeIdx: node.sysNodeIdx,
                queryType: this.queryType,
                queryDate: date ? date : this.today,
            };

            try {
                let nodeTrendData = await backEndApi.sysNode.getSumNodeTrendAll(statusParams);

                if (nodeTrendData.status == 200) {
                    this.sumNodeTotal = nodeTrendData.data;
                } else {
                    this.alertWarning(
                        `${nodeTrendData.data.message}`,
                        "",
                        `${typeof nodeTrendData.data.detail == "string" ? nodeTrendData.data.detail : ""}`
                    );
                }
            } catch (e) {
                console.error(e);
            }
        },
        // baseChartData 초기화 함수
        sumNodeFormatChartDatas() {
            if (!this.isEmpty(this.sumNodeTotal.trendData) && !this.isEmpty(this.sumNodeTotal.trendData.results)) {
                this.baseChartData = trendFunction.sumNodeFormatChartDatas(this.sumNodeTotal.trendData, this.queryType);
            } else {
                this.baseChartData = [];
            }
        },
        // 에너지 유형 설비 노드 일 경우 baseChartData 초기화 함수
        sumNodeFormatChartDatasForEquip() {
            if (!this.isEmpty(this.sumNodeTotal.trendData) && !this.isEmpty(this.sumNodeTotal.trendData.results)) {
                this.baseChartData = trendFunction.sumNodeFormatChartDatasForEquip(
                    this.sumNodeTotal.trendData,
                    this.queryType
                );
            } else {
                this.baseChartData = [];
            }
        },
        async getTrendDataByProp(node, date) {
            this.isChartLoading = false;
            this.baseChartData = [];

            let statusParams = {
                sysNodeIdx: node.sysNodeIdx,
                queryType: this.queryType,
                queryDate: date ? date : this.today,
            };

            const equipName = this.equipStatusData.map((data) => ({
                equipIdx: data.equipIdx,
                equipName: data.equipName,
            }));

            try {
                let nodeTrendData = await backEndApi.sysNode.getGroupTrendData(statusParams);

                if (nodeTrendData.status == 200) {
                    this.groupTrend = nodeTrendData.data;

                    const formatted = trendFunction.formatChartDatasByProp(this.groupTrend, equipName, this.queryType);

                    this.setBaseChartData(formatted);
                } else {
                    this.alertWarning(
                        `${nodeTrendData.data.message}`,
                        "",
                        `${typeof nodeTrendData.data.detail == "string" ? nodeTrendData.data.detail : ""}`
                    );
                }
            } catch (e) {
                console.error(e);
            }
        },
        updateChartOrder(event) {
            // 기존 순서 변경 로직
            const oldIndex = event.moved.oldIndex;
            const newIndex = event.moved.newIndex;
            const movedItem = this.baseChartData.splice(oldIndex, 1)[0];
            this.baseChartData.splice(newIndex, 0, movedItem);
        },
        // 트랜드 2형일때만 차트 순서를 조절한 후 저장하기위해 api call을 하는 함수
        async onChangeOrderTrendSecond() {
            if (!(this.activeIcon === "prop-chart") || !this.currentNode.equipType) return;

            let status = {
                equipType: this.currentNode.equipType,
                propCodes: [],
            };

            this.baseChartData.forEach((chart) => {
                status.propCodes.push(chart.key);
            });
            // 차트 순서 저장 api call
            await this.setPropDispOrder(status);
            this.isOrderChanged = false; // 저장 완료 후 초기화
        },
        async setPropDispOrder(status) {
            await backEndApi.equipTypes.setPropDispOrder(status);
        },
        async getRelatedPointTrendData(node, date) {
            let statusParams = {
                sysNodeIdx: node.sysNodeIdx,
                queryType: this.queryType,
                queryDate: date ? date : this.today,
            };

            try {
                let rltdTrendData = await backEndApi.sysNode.getRelatedPointTrendData(statusParams);
                if (rltdTrendData.status == 200) {
                    this.isChartLoading = true;
                    const check = rltdTrendData.data.results.some((result) =>
                        Object.keys(result).some((key) => {
                            let isNull;
                            if (key !== "regDt") isNull = result[key] !== null;
                            return isNull;
                        })
                    );
                    if (check) {
                        this.rltdTrendData = rltdTrendData.data;
                        if (this.rltdTrendData) {
                            // 영향인자 트랜드가 조회될 때 선택된 영향인자가 없다면 첫번째 영향인자를 선택
                            if (!this.selectedRltdPtIdx && rltdTrendData.data.headers.length > 0) {
                                this.selectedRltdPtIdx = rltdTrendData.data.headers[0].ptIdx;
                            }
                        }
                    } else {
                        this.rltdTrendData = [];
                    }
                } else {
                    this.alertWarning(
                        `${rltdTrendData.data.message}`,
                        "",
                        `${typeof rltdTrendData.data.detail == "string" ? rltdTrendData.data.detail : ""}`
                    );
                }
            } catch (e) {
                console.error(e);
            }
        },
        async showRltdPointTrend(event, hold) {
            if (event) await event.stopPropagation();
            if (!hold) this.isRltdPoint = !this.isRltdPoint;

            if (this.isRltdPoint) {
                let rltdTrendData = await trendFunction.formatRltdChartDatas(this.rltdTrendData, this.queryType);
                this.baseChartData.unshift(...rltdTrendData);
            } else {
                const keysToRemove = new Set(
                    this.rltdTrendData.headers.map((item) => `${item.ptIdx}-${item.dispText}`)
                );
                this.baseChartData = this.baseChartData.filter((data) => !keysToRemove.has(data.key));
            }
        },
        async filterColumns(node, date) {
            let result;
            let statusParams = {
                sysNodeIdx: node.sysNodeIdx,
                queryType: this.queryType,
                queryDate: date ? date : this.today,
            };
            try {
                result = await backEndApi.sysNode.getGroupTrendData(statusParams);
                if (result.status == 200) {
                    let filterChart;
                    if (this.currentNode.nodeType == "Root" || this.currentNode.nodeType == "System") {
                        filterChart = trendFunction.formatSystemDatas(
                            result.data,
                            this.currentNode,
                            this.rows,
                            this.queryType
                        );
                    } else if (this.currentNode.nodeType == "Group") {
                        filterChart = trendFunction.formatChartDatas(
                            result.data,
                            this.currentNode,
                            this.rows,
                            this.queryType
                        );
                    } else if (this.currentNode.nodeType == "Equip") {
                        filterChart = trendFunction.equipFormatChartDatas(
                            result.data,
                            this.equipTypeDetail,
                            this.currentNode,
                            this.rows,
                            this.queryType
                        );
                    } else return;
                    const valuesToRemove = this.chartView.filter((item) => item.checked).map((item) => item.value);
                    filterChart.forEach((chart) => {
                        chart.data.datasets = chart.data.datasets.filter((dataset) =>
                            valuesToRemove.includes(dataset.propCode)
                        );
                    });
                    if (this.isRltdPoint) {
                        let rltdTrendData = await trendFunction.formatRltdChartDatas(
                            this.rltdTrendData,
                            this.queryType
                        );
                        filterChart.unshift(...rltdTrendData);
                    }
                    this.baseChartData = filterChart;
                }
            } catch (e) {
                console.error(e);
            }
            this.closeTableViewSelect();
        },
        async getGroupTrendData(node, date) {
            let statusParams = {
                sysNodeIdx: node.sysNodeIdx,
                queryType: this.queryType,
                queryDate: date ? date : this.today,
            };

            try {
                let nodeTrendData;

                if (this.currentNode.nodeType == "Root" || this.currentNode.nodeType == "System") {
                    nodeTrendData = await backEndApi.sysNode.getSystemTrendData(statusParams);
                } else if (this.currentNode.nodeType == "Group") {
                    nodeTrendData = await backEndApi.sysNode.getGroupTrendData(statusParams);
                    this.trendHeaderData = nodeTrendData.data.headers;
                } else if (this.currentNode.nodeType == "Equip") {
                    nodeTrendData = await backEndApi.sysNode.getEquipTrendData(statusParams);
                    this.trendHeaderData = nodeTrendData.data[node.equipIdx].headers;
                } else return;

                if (nodeTrendData.status == 200) {
                    this.groupTrend = nodeTrendData.data;
                    this.isChartLoading = true;

                    let formatted;

                    if (this.currentNode.nodeType == "Root" || this.currentNode.nodeType == "System") {
                        formatted = trendFunction.formatSystemDatas(
                            this.groupTrend,
                            this.currentNode,
                            this.rows,
                            this.queryType
                        );
                    } else if (this.currentNode.nodeType == "Group") {
                        formatted = trendFunction.formatChartDatas(
                            this.groupTrend,
                            this.currentNode,
                            this.rows,
                            this.queryType
                        );
                    } else if (this.currentNode.nodeType == "Equip") {
                        formatted = trendFunction.equipFormatChartDatas(
                            this.groupTrend,
                            this.equipTypeDetail,
                            this.currentNode,
                            this.rows,
                            this.queryType
                        );
                    } else {
                        formatted = [];
                    }
                    this.setBaseChartData(formatted);
                } else {
                    this.alertWarning(
                        `${nodeTrendData.data.message}`,
                        "",
                        `${typeof nodeTrendData.data.detail == "string" ? nodeTrendData.data.detail : ""}`
                    );
                }
            } catch (e) {
                console.error(e);
            }
        },
    },
};
